"use client";

import { User } from "@/components/Classes/User";
import { ValidateSession } from "@/services/Cookies/FirebaseSession";
import { AccessLevel, PermissionRequirement, UserRole } from "@/types/auth";
import { hasAccessLevel, hasPermission, hasRole, isAdmin, isSuperAdmin } from "@/utils/permissions";
import { ReactElement, useEffect, useState } from "react";
// import { Email, Flag, PersonSearch, SpaceDashboard, InsertPhoto, MonitorHeart, TextSnippet, School, SmartToy, Delete, Schedule } from "@mui/icons-material";
import {
  Email,
  Flag,
  Group,
  Dashboard,
  Business,
  HealthAndSafety,
  Description,
  AccountTree,
  School,
  SmartToy,
  SupportAgent,
  DeleteForever,
  History,
  Schedule,
  ReportProblem,
  Source,
  TravelExplore,
} from "@mui/icons-material";
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import { checkPageAccess } from "@/services/APIs/PermissionManagementService";

type NAV_ITEM = {
	segment: string;
	title: string;
	icon: ReactElement;
};

const ADMIN_NAV: NAV_ITEM[] = [
  {
    segment: "admin",
    title: "Admin Dashboard",
    icon: <Dashboard />,
  },
  {
    segment: "admin/users",
    title: "Users",
    icon: <Group />,
  },
  {
    segment: "admin/company-favicon",
    title: "Company Favicon",
    icon: <Business />,
  },
  {
    segment: "admin/flags",
    title: "Flagged Content",
    icon: <Flag />,
  },
  {
    segment: "admin/health-check",
    title: "Health Check",
    icon: <HealthAndSafety />,
  },
  {
    segment: "admin/resume-template-data",
    title: "Resume Template Data",
    icon: <Description />,
  },
  {
    segment: "admin/email-template",
    title: "Email Template",
    icon: <Email />,
  },
  {
    segment: "admin/process-templates",
    title: "Process Templates",
    icon: <AccountTree />,
  },
  {
    segment: "admin/university-data",
    title: "University Data",
    icon: <School />,
  },
  {
    segment: "admin/ai-management",
    title: "AI Management",
    icon: <SmartToy />,
  },
  {
    segment: "admin/email",
    title: "Email Support for users",
    icon: <SupportAgent />,
  },
  {
    segment: "admin/deletion-requests",
    title: "Deletion Management",
    icon: <DeleteForever />,
  },
  {
    segment: "admin/deleted-users",
    title: "Deleted Users Information",
    icon: <History />,
  },
  {
    segment: "admin/cron-jobs",
    title: "Cron Job Manager",
    icon: <Schedule />,
  },
  {
    segment: "admin/job-sources",
    title: "Application Sources",
    icon: <Source />,
  },
  {
    segment: "admin/extension-failures",
    title: "Extension Failures",
    icon: <ReportProblem />,
  },
  {
    segment: "admin/bing-url-logs",
    title: "Bing URL Logs",
    icon: <TravelExplore />,
  },
  {
    segment: "admin/google-indexing-logs",
    title: "Google Indexing Logs",
    icon: <TravelExplore />,
  },
];

export function useAdminAuth() {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		async function fetchUser() {
			try {
				const userData = await ValidateSession();
				setUser(userData);
			} catch (err) {
				setError("Failed to validate session");
				console.error("Auth error:", err);
			} finally {
				setLoading(false);
			}
		}

		fetchUser();
	}, []);

	// Helper to construct UserClaims from the new User structure
	const getUserClaims = () => {
		if (!user) return null;
		return {
			role: user.userRole || UserRole.USER,
			access: user.accessLevel || AccessLevel.BASIC,
			permissions: user.permissions || [],
		};
	};

	const checkPermission = (requirement: PermissionRequirement): boolean => {
		const claims = getUserClaims();
		if (!claims) return false;
		return hasPermission(claims, requirement);
	};

	const checkIsAdmin = (): boolean => {
		const claims = getUserClaims();
		if (!claims) return false;
		return isAdmin(claims);
	};

	const checkIsSuperAdmin = (): boolean => {
		const claims = getUserClaims();
		if (!claims) return false;
		return isSuperAdmin(claims);
	};

	const checkHasAccessLevel = (level: AccessLevel): boolean => {
		const claims = getUserClaims();
		if (!claims) return false;
		return hasAccessLevel(claims, level);
	};

	const checkHasRole = (role: UserRole): boolean => {
		const claims = getUserClaims();
		if (!claims) return false;
		return hasRole(claims, role);
	};

	const checkHasPermission = (permission: string): boolean => {
		const claims = getUserClaims();
		if (!claims?.permissions) return false;
		return claims.permissions.includes(permission);
	};

	return {
		user,
		loading,
		error,
		isAuthenticated: !!user?.uid,
		claims: getUserClaims(),
		// Permission checking functions
		hasPermission: checkPermission,
		isAdmin: checkIsAdmin,
		isSuperAdmin: checkIsSuperAdmin,
		hasAccessLevel: checkHasAccessLevel,
		hasRole: checkHasRole,
		hasCustomPermission: checkHasPermission,
		// Refresh function
		refresh: async () => {
			setLoading(true);
			try {
				const userData = await ValidateSession();
				setUser(userData);
				setError(null);
			} catch (err) {
				setError("Failed to refresh session");
				console.error("Auth refresh error:", err);
			} finally {
				setLoading(false);
			}
		},
	};
}

// Hook specifically for permission-based rendering
export function usePermission(requirement: PermissionRequirement) {
	const { user, loading, hasPermission } = useAdminAuth();

	return {
		hasAccess: hasPermission(requirement),
		loading,
		user,
	};
}

// Hook for admin-specific functionality
export function useAdminAccess() {
	const { user, loading, isAdmin, isSuperAdmin, hasAccessLevel } = useAdminAuth();

	const [availableNavItems, setAvailableNavItems] = useState<typeof ADMIN_NAV>([]);
	const [loadingNav, setLoadingNav] = useState(true);

	useEffect(() => {
		const filterNavItems = async () => {
			const filteredNav = await Promise.all(
				ADMIN_NAV.map(async (item) => {
					const access = await checkPageAccess(item.segment, user.uid);
					return access.hasAccess ? item : null;
				})
			);

			setLoadingNav(false);
			return filteredNav.filter(Boolean);
		};

		if (user?.uid && isAdmin()) {
			filterNavItems().then((items) => setAvailableNavItems(items));
		}
	}, [user?.uid]);

	return {
		isAdmin: isAdmin(),
		isSuperAdmin: isSuperAdmin(),
		hasBasicAccess: hasAccessLevel(0),
		hasAdvancedAccess: hasAccessLevel(1),
		hasSuperAccess: hasAccessLevel(2),
		availableNavItems,
		loading,
		loadingNav,
		user,
	};
}
